import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError, api } from '../../api/client.js';
import ParcelMap, { plotSize } from '../../components/ParcelMap.jsx';
import PlanChecks from '../../components/PlanChecks.jsx';
import { ErrorState, Field, Spinner, StatusBadge } from '../../components/ui.jsx';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useMeta } from '../../state/MetaContext.jsx';
import { useToast } from '../../state/ToastContext.jsx';

const BLANK = {
  village: '',
  city: '',
  chiefName: '',
  chiefPhone: '',
  chiefSignedOn: '',
  layoutId: '',
  plotNumber: '',
  certificateNumber: ''
};

/**
 * The side lengths printed on the plan, typed in order: "25.33, 20, 25.33, 20",
 * "25,33 ; 20,00" or one per line all read the same.
 */
const parseSides = (text) => {
  const pieces = String(text || '').trim().split(/[\s;]+/).filter(Boolean);
  const decimalCommas = !text.includes('.') && pieces.length > 1 && pieces.every((piece) => /^\d+,\d+$/.test(piece));
  return pieces
    .flatMap((piece) => (decimalCommas ? [piece.replace(',', '.')] : piece.split(',')))
    .map((value) => Number(value.replace(/[^\d.]/g, '')))
    .filter((value) => Number.isFinite(value) && value > 0);
};

const VERIFY_BADGE = {
  pending: { status: 'pending', label: 'Waiting to be checked' },
  verified: { status: 'approved', label: 'Verified' },
  rejected: { status: 'rejected', label: 'Turned down' }
};

/** One file input that uploads as soon as a file is picked. */
const UploadButton = ({ label, onFile, busy }) => (
  <label className={`btn btn-outline btn-sm ${busy ? 'is-busy' : ''}`}>
    {busy ? 'Uploading...' : label}
    <input type="file" accept="image/*,application/pdf" hidden disabled={busy} onChange={(event) => onFile(event.target.files?.[0])} />
  </label>
);

/**
 * Registering a plot, or putting a registration right.
 *
 * The boundary is the heart of it: the beacons off the chief-signed site plan,
 * clicked on the photograph or typed in, in degrees or as UTM. As it is drawn
 * the register is asked whether any of it is somebody else's, so a double claim
 * shows up on the map before anything is saved.
 */
const RegisterPlot = () => {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();
  const { cities, city: lookupCity } = useMeta();
  useTitle(editing ? 'Your plot' : 'Register a plot');

  const mine = useFetch('/land/mine');
  // Whether plans can be read automatically here.
  const reader = useFetch(editing ? null : '/land/read-plan');
  const [reading, setReading] = useState(null);
  const [readingBusy, setReadingBusy] = useState(false);
  const existing = useFetch(editing ? `/land/parcels/${id}` : null, [id]);

  const [form, setForm] = useState(BLANK);
  const [points, setPoints] = useState([]);
  const [entry, setEntry] = useState('map');
  const [typed, setTyped] = useState('');
  // Blank: let the register work the zone out from the numbers and the town.
  const [utmZone, setUtmZone] = useState('');
  const [typedRead, setTypedRead] = useState(null);
  // What the plan prints, to check the beacons against.
  const [sidesText, setSidesText] = useState('');
  const [areaText, setAreaText] = useState('');
  const [check, setCheck] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [newLayout, setNewLayout] = useState(null);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  useEffect(() => {
    const parcel = existing.data?.parcel;
    if (!parcel) return;
    setForm({
      village: parcel.village || '',
      city: parcel.city || '',
      chiefName: parcel.chiefName || '',
      chiefPhone: parcel.chiefPhone || '',
      chiefSignedOn: parcel.chiefSignedOn || '',
      layoutId: parcel.layout?.id ? String(parcel.layout.id) : '',
      plotNumber: parcel.plotNumber || '',
      certificateNumber: parcel.certificateNumber || ''
    });
    setPoints(parcel.boundary || []);
  }, [existing.data]);

  // Ask the register about the boundary as it is drawn. Typed beacons are
  // checked when they are read instead, in the units they were typed in - a
  // second check here would measure them again in degrees.
  useEffect(() => {
    if (entry === 'typed' && typedRead) return undefined;
    if (points.length < 3) {
      setCheck(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      api
        .post('/land/check', {
          boundary: points,
          sides: parseSides(sidesText),
          statedArea: Number(areaText) || null,
          exceptId: editing ? Number(id) : undefined
        })
        .then(setCheck)
        .catch((error) => setCheck({ error: error.message }));
    }, 350);
    return () => clearTimeout(timer);
  }, [points, editing, id, sidesText, areaText, entry, typedRead]);

  const parcel = existing.data?.parcel;
  const layouts = mine.data?.layouts || [];
  const town = lookupCity(form.city);

  const plots = useMemo(
    () => [
      ...(check?.clashes || []).map((clash) => ({
        id: `clash-${clash.id}`,
        boundary: clash.boundary,
        tone: 'clash',
        label: `${clash.reference} - already registered${clash.yours ? ' to you' : ''}`
      }))
    ],
    [check]
  );

  if ((mine.loading && !mine.data) || (editing && existing.loading && !existing.data)) return <Spinner />;
  if (existing.error) {
    return (
      <div className="container section">
        <ErrorState message={existing.error} />
      </div>
    );
  }

  // The beacon table as it was typed or pasted - the register works out what
  // the numbers are, and checks them against the plan's sides and area.
  const useTyped = async (text = typed) => {
    try {
      const result = await api.post('/land/check', {
        beaconText: text,
        town: form.city || null,
        utmZone: utmZone ? Number(utmZone) : null,
        sides: parseSides(sidesText),
        statedArea: Number(areaText) || null,
        exceptId: editing ? Number(id) : undefined
      });
      setTypedRead(result.read);
      setPoints(result.boundary);
      setCheck(result);
      toast.success(`${result.boundary.length} beacons placed. Check them on the map.`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Putting a mistyped beacon where the plan's sides say it has to be.
  const applyCorrection = (correction) => {
    if (!correction) return;
    if (entry === 'typed' && typedRead?.typed?.length) {
      const rows = typedRead.typed.map((row, index) => (index === correction.index ? correction.typed : row));
      const text = rows.map((row, index) => `${typedRead.labels[index]}  ${row[0]}  ${row[1]}`).join('\n');
      setTyped(text);
      useTyped(text);
      return;
    }
    setPoints((current) => current.map((point, index) => (index === correction.index ? correction.point : point)));
    toast.success(`${correction.label} moved to where the plan's sides put it.`);
  };

  // Upload the site plan and fill the form from what is read on it.
  const readPlan = async (file) => {
    if (!file) return;
    setReadingBusy(true);
    try {
      const data = new FormData();
      if (form.city) data.append('town', form.city);
      data.append('file', file);
      const result = await api.upload('/land/read-plan', data);
      setReading(result);
      if (result.error) {
        toast.error(result.error);
        return;
      }
      const read = result.read || {};
      const townMatch = cities.find((item) =>
        [read.town, read.village].some((value) => value && value.toLowerCase().includes(item.name.toLowerCase()))
      );
      const layoutMatch = read.layoutName
        ? layouts.find((layout) => layout.name.toLowerCase() === read.layoutName.toLowerCase())
        : null;
      setForm((current) => ({
        ...current,
        village: read.village || current.village,
        city: townMatch?.name || current.city,
        chiefName: read.chiefName || current.chiefName,
        chiefSignedOn: /^\d{4}-\d{2}-\d{2}$/.test(read.signedOn || '') ? read.signedOn : current.chiefSignedOn,
        plotNumber: read.plotNumber || current.plotNumber,
        certificateNumber: read.certificateNumber || current.certificateNumber,
        layoutId: layoutMatch ? String(layoutMatch.id) : current.layoutId
      }));
      if (result.boundary?.length >= 3) setPoints(result.boundary);
      if ((read.sideLengths || []).some((value) => value)) setSidesText(read.sideLengths.map((value) => value ?? '').join(', '));
      if (read.statedAreaSqm) setAreaText(String(Math.round(read.statedAreaSqm)));
      toast.success(
        result.verdict === 'looks_good'
          ? 'Site plan read. Check each figure against the plan, then register.'
          : 'Site plan read, with some things to check - see below.'
      );
    } catch (error) {
      toast.error(error.message);
    } finally {
      setReadingBusy(false);
    }
  };

  const createLayout = async () => {
    try {
      const { layout } = await api.post('/land/layouts', {
        name: newLayout.name,
        village: newLayout.village || form.village,
        city: form.city || null,
        chiefName: newLayout.chiefName || form.chiefName,
        chiefPhone: newLayout.chiefPhone || null
      });
      toast.success('Layout added. Upload its general plan below.');
      setNewLayout(null);
      await mine.reload();
      set({ layoutId: String(layout.id) });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const uploadTo = async (route, file, key, message) => {
    if (!file) return;
    setUploading(key);
    try {
      const data = new FormData();
      data.append('file', file);
      await api.upload(route, data);
      toast.success(message);
      existing.reload?.();
      mine.reload();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(null);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    setErrors({});
    if (points.length < 3) {
      setErrors({ boundary: 'Mark at least three beacons.' });
      return;
    }
    setSaving(true);
    const payload = {
      village: form.village.trim(),
      city: form.city || null,
      chiefName: form.chiefName.trim(),
      chiefPhone: form.chiefPhone.trim() || null,
      chiefSignedOn: form.chiefSignedOn || null,
      layoutId: form.layoutId ? Number(form.layoutId) : null,
      plotNumber: form.plotNumber.trim() || null,
      certificateNumber: form.certificateNumber.trim() || null,
      boundary: points,
      readingId: !editing && reading?.readingId && !reading.error ? reading.readingId : null
    };
    try {
      if (editing) {
        await api.patch(`/land/parcels/${id}`, payload);
        toast.success('Saved. It goes back to be checked again.');
        existing.reload();
      } else {
        const result = await api.post('/land/parcels', payload);
        toast.success('Plot registered. Now upload the site plan the chief signed.');
        navigate(`/my/land/${result.parcel.id}`, { replace: true });
      }
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const chosenLayout = layouts.find((layout) => String(layout.id) === form.layoutId);
  const clashing = (check?.clashes || []).length > 0;

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>{editing ? `Plot ${parcel?.reference || ''}` : 'Register a plot'}</h1>
          <p className="muted">
            Every plot sold here is registered once, from the site plan its village chief signed. Nobody else can register
            or sell the same ground after you.
          </p>
        </div>
        <div className="row-actions">
          {editing && parcel?.verified ? (
            <Link to={`/land/${id}`} className="btn btn-ghost">
              Public history
            </Link>
          ) : null}
          <Link to="/my/land" className="btn btn-ghost">
            My land
          </Link>
        </div>
      </header>

      {parcel ? (
        <div className={`notice notice-${parcel.verificationStatus === 'verified' ? 'good' : parcel.verificationStatus === 'rejected' ? 'bad' : 'info'}`}>
          <StatusBadge {...VERIFY_BADGE[parcel.verificationStatus]} />{' '}
          {parcel.verificationStatus === 'verified'
            ? 'Checked against the site plan. You can put it up for sale.'
            : parcel.verificationStatus === 'rejected'
              ? `Turned down: ${parcel.verificationNote || 'see the note from the team.'} Put it right and save to send it again.`
              : parcel.hasSitePlan
                ? 'Our team compares the chief-signed site plan with the boundary you drew, usually within a day.'
                : 'Upload the site plan the chief signed. Nothing is checked until it is in.'}
        </div>
      ) : null}

      <form className="editor-layout" onSubmit={submit}>
        <div className="editor-main">
          {!editing && reader.data?.enabled ? (
            <section className="panel reader-panel">
              <div className="section-head">
                <div>
                  <h2>Read my site plan for me</h2>
                  <p className="field-hint">
                    Upload the site plan the chief signed - a clear photo or a scan. We read the village, the chief, the
                    plot and certificate numbers and the beacons off it, fill this form in, and check it. You correct
                    anything we got wrong.
                  </p>
                </div>
                <label className={`btn btn-primary ${readingBusy ? 'is-busy' : ''}`}>
                  {readingBusy ? 'Reading the plan...' : reading ? 'Read another plan' : 'Upload the site plan'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    hidden
                    disabled={readingBusy}
                    onChange={(event) => readPlan(event.target.files?.[0])}
                  />
                </label>
              </div>
              {readingBusy ? <p className="muted">This takes up to a minute for a detailed plan.</p> : null}
              {reading ? <PlanChecks analysis={reading} /> : null}
              {reading?.planCheck?.correction ? (
                <button type="button" className="btn btn-outline btn-sm" onClick={() => applyCorrection(reading.planCheck.correction)}>
                  Use the corrected {reading.planCheck.correction.label}
                </button>
              ) : null}
              {reading && !reading.error ? (
                <p className="field-hint">
                  This plan is kept and attached to the plot when you register it - no need to upload it again.
                </p>
              ) : null}
            </section>
          ) : null}

          {editing && parcel?.planAnalysis ? (
            <section className="panel">
              <PlanChecks analysis={parcel.planAnalysis} title="What was read on the site plan on file" />
            </section>
          ) : null}

          <section className="panel">
            <h2>Where it is</h2>
            <div className="grid-3">
              <Field label="Village or quarter" error={errors.village} required>
                <input type="text" value={form.village} onChange={(event) => set({ village: event.target.value })} required placeholder="Bokwai" />
              </Field>
              <Field label="Town" error={errors.city}>
                <select value={form.city} onChange={(event) => set({ city: event.target.value })}>
                  <option value="">Choose a town</option>
                  {cities.map((item) => (
                    <option key={item.name} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Land certificate number" hint="The titre foncier number, if it has one." error={errors.certificateNumber}>
                <input type="text" value={form.certificateNumber} onChange={(event) => set({ certificateNumber: event.target.value })} />
              </Field>
            </div>
          </section>

          <section className="panel">
            <h2>The chief who signed the site plan</h2>
            <p className="field-hint">
              Kept private. Only somebody who reports a conflict over the land is shown who to go to.
            </p>
            <div className="grid-3">
              <Field label="Chief's name" error={errors.chiefName} required>
                <input type="text" value={form.chiefName} onChange={(event) => set({ chiefName: event.target.value })} required placeholder="HRH Chief ..." />
              </Field>
              <Field label="Chief's phone" error={errors.chiefPhone}>
                <input type="tel" value={form.chiefPhone} onChange={(event) => set({ chiefPhone: event.target.value })} />
              </Field>
              <Field label="Date the chief signed" error={errors.chiefSignedOn}>
                <input type="date" value={form.chiefSignedOn} onChange={(event) => set({ chiefSignedOn: event.target.value })} />
              </Field>
            </div>
          </section>

          <section className="panel">
            <h2>Is it in a new layout?</h2>
            <p className="field-hint">
              A layout is a piece of land cut into plots. It has a general site plan of its own, which is checked first.
            </p>
            <div className="grid-2">
              <Field label="Layout" error={errors.layoutId}>
                <select value={form.layoutId} onChange={(event) => set({ layoutId: event.target.value })}>
                  <option value="">Not in a layout</option>
                  {layouts.map((layout) => (
                    <option key={layout.id} value={layout.id}>
                      {layout.name}, {layout.village}
                      {layout.verified ? '' : ' (being checked)'}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Plot number" hint={form.layoutId ? 'As it is on the general plan.' : undefined} error={errors.plotNumber}>
                <input type="text" value={form.plotNumber} onChange={(event) => set({ plotNumber: event.target.value })} />
              </Field>
            </div>

            {chosenLayout?.yours ? (
              <div className="site-plan-box">
                <div>
                  <strong>General plan of {chosenLayout.name}</strong>
                  <p className="field-hint">
                    {chosenLayout.hasGeneralPlan ? 'On file.' : 'Not uploaded yet - plots in it cannot be verified until it is.'}{' '}
                    {chosenLayout.verified ? 'Checked.' : ''}
                  </p>
                  {chosenLayout.generalPlanUrl ? (
                    <a href={chosenLayout.generalPlanUrl} target="_blank" rel="noreferrer">
                      Open the general plan
                    </a>
                  ) : null}
                </div>
                <UploadButton
                  label={chosenLayout.hasGeneralPlan ? 'Replace it' : 'Upload the general plan'}
                  busy={uploading === 'layout'}
                  onFile={(file) => uploadTo(`/land/layouts/${chosenLayout.id}/plan`, file, 'layout', 'General plan saved.')}
                />
              </div>
            ) : null}

            {newLayout ? (
              <div className="site-plan-box">
                <div className="grid-3" style={{ flex: 1 }}>
                  <Field label="Layout name" required>
                    <input type="text" value={newLayout.name} onChange={(event) => setNewLayout({ ...newLayout, name: event.target.value })} />
                  </Field>
                  <Field label="Village">
                    <input type="text" value={newLayout.village} placeholder={form.village} onChange={(event) => setNewLayout({ ...newLayout, village: event.target.value })} />
                  </Field>
                  <Field label="Chief who signed it">
                    <input type="text" value={newLayout.chiefName} placeholder={form.chiefName} onChange={(event) => setNewLayout({ ...newLayout, chiefName: event.target.value })} />
                  </Field>
                </div>
                <div className="row-actions">
                  <button type="button" className="btn btn-primary btn-sm" onClick={createLayout} disabled={!newLayout.name.trim()}>
                    Add the layout
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setNewLayout(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" className="link-btn" onClick={() => setNewLayout({ name: '', village: '', chiefName: '', chiefPhone: '' })}>
                + It is in a new layout that is not listed
              </button>
            )}
          </section>

          <section className="panel">
            <div className="section-head">
              <h2>The boundary</h2>
              <div className="view-toggle" role="group" aria-label="How to enter the beacons">
                {[
                  ['map', 'Click on the map'],
                  ['typed', 'Type the coordinates']
                ].map(([value, label]) => (
                  <button key={value} type="button" className={entry === value ? 'is-current' : ''} onClick={() => setEntry(value)}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {entry === 'typed' ? (
              <div className="stack">
                <p className="field-hint">
                  Copy the beacon table off the site plan just as it is - names, X and Y, commas or full stops, with or
                  without the header. We work out whether they are UTM metres or degrees, and which zone.
                </p>
                <Field label="Beacon table" error={errors.beaconText}>
                  <textarea
                    rows="7"
                    className="mono-input"
                    value={typed}
                    onChange={(event) => setTyped(event.target.value)}
                    placeholder={'BEA No   X              Y\nB1   520639.0921   445491.9043\nB2   520664.3592   445491.0595\nB3   520665.1889   445471.1617\nB4   520639.8702   445471.9194'}
                  />
                </Field>
                <div className="grid-3">
                  <Field label="UTM zone" hint="Leave it to us unless the plan prints the zone (fuseau).">
                    <select value={utmZone} onChange={(event) => setUtmZone(event.target.value)}>
                      <option value="">Work it out</option>
                      <option value="32">32N - the coast, the west, Douala</option>
                      <option value="33">33N - Yaounde, the centre and east</option>
                    </select>
                  </Field>
                </div>
                <div>
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => useTyped()} disabled={!typed.trim()}>
                    Read these beacons
                  </button>
                </div>
                {typedRead?.notes?.length ? (
                  <ul className="read-notes">
                    {typedRead.notes.map((note) => (
                      <li key={note}>{note}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : (
              <p className="field-hint">
                Zoom in on the satellite photograph and click each beacon in turn, going round the plot. Use the site plan
                and what you can see on the ground: fences, paths, the road.
              </p>
            )}

            <ParcelMap
              plots={plots}
              drawing={points}
              onAddPoint={entry === 'map' ? (point) => setPoints((current) => [...current, point]) : undefined}
              center={points[0] || (town ? [town.lat, town.lng] : undefined)}
              height={460}
            />

            <div className="boundary-bar">
              <span>
                {points.length} beacon{points.length === 1 ? '' : 's'}
                {check?.areaSqm ? ` - ${plotSize(check.areaSqm)}` : ''}
              </span>
              <div className="row-actions">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPoints((current) => current.slice(0, -1))} disabled={!points.length}>
                  Undo last
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPoints([])} disabled={!points.length}>
                  Start again
                </button>
              </div>
            </div>
            <div className="plan-figures">
              <strong>Check against the plan</strong>
              <p className="field-hint">
                Type the side lengths printed along the boundary, in order from the first beacon, and the area. Each side is
                compared with the beacons, so a mistyped figure shows up - and gets put right.
              </p>
              <div className="grid-2">
                <Field label="Side lengths in metres" hint="For example: 25.33, 20.00, 25.33, 20.00">
                  <input type="text" value={sidesText} onChange={(event) => setSidesText(event.target.value)} />
                </Field>
                <Field label="Area on the plan, m2">
                  <input type="number" min="0" value={areaText} onChange={(event) => setAreaText(event.target.value)} />
                </Field>
              </div>
            </div>

            {check?.planCheck ? (
              <div className={`plan-checks is-${check.planCheck.correction || check.planCheck.sides.some((side) => side.ok === false) || check.planCheck.areaOk === false ? 'attention' : 'good'}`}>
                {check.planCheck.sides.length ? (
                  <table className="sides-table">
                    <thead>
                      <tr>
                        <th>Side</th>
                        <th>On the plan</th>
                        <th>From the beacons</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {check.planCheck.sides.map((side) => (
                        <tr key={`${side.from}-${side.to}`} className={side.ok === false ? 'is-flag' : ''}>
                          <td>
                            {side.from} - {side.to}
                          </td>
                          <td>{side.printed != null ? `${side.printed} m` : '-'}</td>
                          <td>{side.measured} m</td>
                          <td>{side.ok == null ? '' : side.ok ? '✓' : 'Does not match'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : null}
                {check.planCheck.statedArea ? (
                  <p className={check.planCheck.areaOk ? 'muted' : 'overdue-flag'}>
                    The beacons enclose {check.planCheck.areaSqm} m2; the plan says {check.planCheck.statedArea} m2.
                  </p>
                ) : null}
                {check.planCheck.correction ? (
                  <div className="correction-card">
                    <div>
                      <strong>{check.planCheck.correction.label} looks mistyped.</strong>{' '}
                      Its two sides do not match the plan while the others do. To fit the plan it should be at{' '}
                      <code>
                        {check.planCheck.correction.typed[0]}, {check.planCheck.correction.typed[1]}
                      </code>{' '}
                      - {check.planCheck.suspect?.movedBy} m from where it is now - giving {check.planCheck.correction.areaSqm} m2.
                    </div>
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => applyCorrection(check.planCheck.correction)}>
                      Use the corrected {check.planCheck.correction.label}
                    </button>
                  </div>
                ) : check.planCheck.suspect ? (
                  <p className="overdue-flag">
                    {check.planCheck.suspect.label} looks wrong. Check those figures against the plan.
                  </p>
                ) : check.planCheck.sides.length && check.planCheck.sides.every((side) => side.ok !== false) ? (
                  <p className="muted">Every side matches the plan.</p>
                ) : null}
              </div>
            ) : null}

            {errors.boundary ? <p className="field-error-text">{errors.boundary}</p> : null}
            {check?.error ? <div className="notice notice-bad">{check.error}</div> : null}
            {clashing ? (
              <div className="notice notice-bad">
                <strong>This land is already on the register.</strong> The red shape is{' '}
                {check.clashes.map((clash) => clash.reference).join(', ')} ({check.clashes[0].sharedSqm} m2 in common). It
                cannot be registered or sold twice.{' '}
                {check.clashes[0].yours ? 'It is registered to you already.' : <Link to={`/land/${check.clashes[0].id}`}>See that plot and report a conflict.</Link>}
              </div>
            ) : points.length >= 3 && check && !check.error ? (
              <div className="notice notice-good">No registered plot overlaps this boundary.</div>
            ) : null}
          </section>
        </div>

        <aside className="editor-side">
          <div className="panel sticky-panel">
            <h2>{editing ? 'Save and send' : 'Register'}</h2>
            <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={saving || clashing}>
              {saving ? 'Saving...' : editing ? 'Save changes' : 'Register the plot'}
            </button>
            {editing ? (
              <p className="field-hint">Saving a change sends the plot to be checked again.</p>
            ) : (
              <p className="field-hint">
                {reading?.readingId && !reading.error
                  ? 'The site plan you had read is attached when you register.'
                  : 'Next you upload the site plan the chief signed.'}
              </p>
            )}

            <div className="site-plan-box">
              <div>
                <strong>Site plan signed by the chief</strong>
                <p className="field-hint">Required. A clear photo or scan showing the chief's signature and stamp.</p>
                {parcel?.sitePlanUrl ? (
                  <a href={parcel.sitePlanUrl} target="_blank" rel="noreferrer">
                    Open the plan on file
                  </a>
                ) : null}
              </div>
              {!editing && reading?.readingId && !reading.error ? (
                <p className="field-hint">The plan you had read will be attached.</p>
              ) : editing ? (
                <UploadButton
                  label={parcel?.hasSitePlan ? 'Replace it' : 'Upload it'}
                  busy={uploading === 'plan'}
                  onFile={(file) => uploadTo(`/land/parcels/${id}/site-plan`, file, 'plan', 'Site plan saved. It is now waiting to be checked.')}
                />
              ) : (
                <p className="field-hint">You can upload it once the plot is registered.</p>
              )}
            </div>

            <ul className="checklist">
              <li className={form.village && form.chiefName ? 'is-done' : ''}>Village and chief</li>
              <li className={points.length >= 3 && !clashing ? 'is-done' : ''}>Boundary marked, overlapping nobody</li>
              <li className={parcel?.hasSitePlan || (reading?.readingId && !reading.error) ? 'is-done' : ''}>Chief-signed site plan uploaded</li>
              {form.layoutId ? <li className={chosenLayout?.hasGeneralPlan ? 'is-done' : ''}>Layout general plan on file</li> : null}
              <li className={parcel?.verified ? 'is-done' : ''}>Checked by our team</li>
            </ul>
          </div>
        </aside>
      </form>
    </div>
  );
};

export default RegisterPlot;
